<template>
    <div v-if="ticket" class="page ticket-scroll-page">
        <Navigation pageName="ticket" :data="ticket ? { 'ticketID': ticket.id, 'ticketName': ticket.title } : {}" />

        <div class="ticket-page p-5">

            <div class="mb-4 text-right">
                <button v-if="!isNewTicket" class="button alert" style="float:left" @click="deleteTicket">{{ $t("Delete") }}</button>
                <router-link class="button" to="/tickets">{{ $t("Cancel") }}</router-link>
                <button v-if="!isReadOnlyTicket" class="button secondary ml-2" :disabled="saving || !canSaveTicket()" @click="save">
                    {{ $t("Save") }}
                </button>
            </div>

            <div class="ticket-form">
                <section class="ticket-section">
                    <div class="ticket-section-header">
                        <h5 class="m-0 text-bold">{{ $t("Ticket information") }}</h5>
                    </div>
                    <div class="ticket-section-body">
                        <div v-if="!isNewTicket" class="row mb-2">
                            <div class="cell-4 text-bold">ID:</div>
                            <div class="cell-8">
                                <p class="m-0">{{ ticket.id || "-" }}</p>
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="cell-4 text-bold">{{ $t("Title") }}:</div>
                            <div class="cell-8">
                                <input v-if="isNewTicket" class="input w-100" v-model="ticket.title" />
                                <h5 class="text-bold" v-else>{{ ticket.title }}</h5>
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="cell-4 text-bold">{{ $t("Description") }}:</div>
                            <div class="cell-8">
                                <textarea v-if="isNewTicket" class="input w-100" rows="5" v-model="ticket.description"></textarea>
                                <p v-else>{{ ticket.description }}</p>
                            </div>
                        </div>
                    </div>
                </section>
                <section class="ticket-section">
                    <div class="ticket-section-header">
                        <h5 class="m-0 text-bold">{{ $t("Customer") }}</h5>
                    </div>
                    <div class="ticket-section-body">
                        <div class="row mb-2">
                            <div class="cell-4 text-bold">{{ $t("Customer") }}:</div>
                            <div class="cell-8">
                                <p v-if="isReadOnlyTicket" class="m-0">{{ customerLabel }}</p>
                                <div v-else class="d-flex flex-align-center">
                                    <select :disabled="!isNewTicket && !!ticket.id_customer" class="select customer-select" v-model="selectedCustomer" @change="onChangeCustomer">
                                        <option v-for="customer in customers" :value="customer" :key="customer.id" :selected="ticket.id_customer == customer.id">{{ customer.company_name }}</option>
                                    </select>
                                    <button v-if="selectedCustomer" type="button" class="button ml-2" :title="$t('Edit')" @click="editSelectedCustomer">
                                        <span class="mif-pencil"></span>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div v-if="isReadOnlyTicket && ticket.id_remote" class="row mb-2">
                            <div class="cell-4 text-bold">{{ $t("Device") }}:</div>
                            <div class="cell-8">
                                <p class="m-0">{{ selectedPC ? selectedPC.pc_name + " (" + selectedPC.id_remote + ")" : deviceLabel }}</p>
                                <!--<select v-if="ticket.id_customer" class="select w-100" v-model="selectedPC" @change="onChangePC">
                                    <option v-for="pc in customerPCs" :value="pc" :key="pc.id" :selected="ticket.id_remote == pc.id_remote">{{ pc.pc_name }} ({{ pc.id_remote }})</option>
                                </select>-->
                                <!-- Senza cliente assegnato non possiamo costruire la lista PC del cliente:
                                     se il ticket ha un id_remote, mostriamo il PC come valore testuale. -->
                                <!--<div v-else class="pt-1">{{ deviceLabel || "—" }}</div>-->
                            </div>
                        </div>

                        <div v-else-if="ticket.id_customer" class="row mb-2">
                            <div class="cell-4 text-bold">{{ $t("Device") }}:</div>
                            <div class="cell-8">
                                <SelectPCByGroup
                                    v-if="customerGroups.length>0"
                                    :key="groupSelectorKey"
                                    :idCustomer="ticket.id_customer"
                                    :idPC="selectedPCId"
                                    :groupLabel="$t('Select a Group')"
                                    :pcLabel="$t('Select a Device')"
                                    :selectIfOnline="false"
                                    @selectPC="onChangePC" />
                                <div v-else class="customer-groups-warning warning p-3 rounded">
                                    <span class="mif-warning mr-2"></span>
                                    {{ $t('The selected customer has no assigned groups. Edit the customer to assign at least one group.') }}
                                </div>
                            </div>
                        </div>

                        <div v-if="ticket.customer_notes != ''" class="row mb-2">
                            <div class="cell-4 text-bold">{{ $t("Notes") }}:</div>
                            <div class="cell-8">
                                {{ ticket.customer_notes }}
                            </div>
                        </div>
                    </div>
                </section>

                <section class="ticket-section">
                    <div class="ticket-section-header">
                        <h5 class="m-0 text-bold">{{ $t("Properties") }}</h5>
                    </div>
                    <div class="ticket-section-body">
                        <div class="row mb-2">
                            <div class="cell-4 text-bold">{{ $t("Priority") }}:</div>
                            <div class="cell-8">
                                <p v-if="isReadOnlyTicket" class="m-0">
                                    <span class="tk-badge" :style="TICKET_PRIORITY_INFO[ticket.priority].badge_style">
                                        {{ $t(TICKET_PRIORITY_INFO[ticket.priority].label) }}
                                    </span>
                                </p>
                                <select v-else class="select w-100" v-model.number="ticket.priority">
                                    <option v-for="priority in priorityOptions" :value="priority.value" :key="priority.value">{{ $t(priority.label) }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="cell-4 text-bold">{{ $t("Status") }}:</div>
                            <div class="cell-8">
                                <p v-if="isReadOnlyTicket" class="m-0">
                                    <span class="tk-badge" :style="TICKET_STATUS_INFO[ticket.status].badge_style">
                                        {{ $t(TICKET_STATUS_INFO[ticket.status].label) }}
                                    </span>
                                </p>
                                <select v-else :disabled="isNewTicket && !ticket.id_customer" class="select w-100" v-model.number="ticket.status" @change="onChangeStatus">
                                    <option v-for="status in selectableStatusOptions" :value="status" :key="status" :disabled="initialStatus == status">{{ $t(TICKET_STATUS_INFO[status].label) }}</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </section>

                <section class="ticket-section">
                    <div class="ticket-section-header">
                        <h5 class="m-0 text-bold">{{ $t("Operator") }}</h5>
                    </div>
                    <div class="ticket-section-body">
                        <div class="row mb-2">
                            <div class="cell-4 text-bold">{{ $t("Team") }}:</div>
                            <div class="cell-8">
                                <p v-if="isReadOnlyTicket" class="m-0">{{ teamLabel }}</p>
                                <select v-else class="select w-100" v-model="selectedTeam" :disabled="ticket.id_customer == '' || teams.length == 0 || isUnassignedStatus(ticket.status)" @change="onChangeTeam">
                                    <option v-for="t in teams" :value="t" :key="t.id" :selected="ticket.id_team == t.id">{{ t.name }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="cell-4 text-bold">{{ $t("Operator") }}:</div>
                            <div class="cell-8">
                                <p v-if="isReadOnlyTicket" class="m-0">{{ operatorLabel }}</p>
                                <select v-else class="select w-100" v-model="selectedOperator" @change="onChangeOperator" :disabled="isUnassignedStatus(ticket.status) || !userLoggedIsLead">
                                    <option v-for="u in operators" :value="u" :key="u.id" :selected="ticket.id_user_assigned == u.id">{{ userLabel(u) }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="cell-4 text-bold">{{ $t("Operator notes") }}:</div>
                            <div class="cell-8">
                                <p v-if="isReadOnlyTicket" class="m-0">{{ ticket.operator_notes || "-" }}</p>
                                <textarea v-else class="input w-100" rows="4" v-model="ticket.operator_notes"></textarea>
                            </div>
                        </div>
                    </div>
                </section>

                <section v-if="!isNewTicket" class="ticket-section">
                    <div class="ticket-section-header d-flex flex-justify-between flex-align-center">
                        <h5 class="m-0 text-bold">{{ $t("Sessions") }}</h5>
                        <div v-if="ticketSessions.length > 0" class="text-small">
                            <span class="mr-3">
                                <span class="mif-shrink2 mr-1"></span>{{ ticketSessions.length }}
                            </span>
                            <span>
                                <span class="mif-hour-glass mr-1"></span>{{ formatHours(ticketSessionsTotalTime) }}
                            </span>
                        </div>
                    </div>
                    <div class="ticket-section-body">
                        <div v-if="ticketSessions.length == 0" class="text-small fg-dark">
                            {{ $t("No sessions for this ticket.") }}
                        </div>
                        <table v-else class="border bg-white compact table w-100 ticket-sessions-table">
                            <thead class="mb-0">
                                <tr class="mb-3">
                                    <th class="pl-5" style="vertical-align: middle;">{{ $t("Operator") }}</th>
                                    <th class="text-center" style="vertical-align: middle;">{{ $t("Source") }}</th>
                                    <th class="text-center" style="vertical-align: middle;"></th>
                                    <th class="text-center" style="vertical-align: middle;">{{ $t("Destination") }}</th>
                                    <th class="text-center" style="vertical-align: middle;">{{ $t("Start date") }}</th>
                                    <th class="text-center" style="vertical-align: middle;">{{ $t("End Date") }}</th>
                                    <th class="text-right pr-5" style="vertical-align: middle;">{{ $t("Duration") }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(session, index) in ticketSessions" :key="session.id || index">
                                    <td class="text-bold text-left pl-5">
                                        {{ sessionUserLabel(session) }}
                                    </td>
                                    <td class="ticket-session-pc d-flex flex-column flex-align-center">
                                        <div class="ticket-session-device-line text-bold">
                                            <span class="mif-display mif-2x mr-1"></span>
                                            <span>{{ session.name_computer_control || "-" }}</span>
                                        </div>
                                        <div class="ticket-session-device-line text-small fg-dark">
                                            <img class="ticket-session-remote-icon mr-1" src="/assets/img/icons/logo_remote.svg" alt="Iperius Remote">
                                            <span>{{ IDRemoteWithSpace(session.id_remote_control) }}</span>
                                        </div>
                                    </td>
                                    <td style="vertical-align: middle;text-align: center;">
                                        <span class="mif mif-2x mif-arrow-right text-center"></span>
                                    </td>
                                    <td class="ticket-session-pc d-flex flex-column flex-align-center">
                                        <div class="ticket-session-device-line text-bold">
                                            <span class="mif-display mif-2x mr-1"></span>
                                            <span>{{ session.name_computer_host || session.custom_name || "-" }}</span>
                                        </div>
                                        <div class="ticket-session-device-line text-small fg-dark">
                                            <img class="ticket-session-remote-icon mr-1" src="/assets/img/icons/logo_remote.svg" alt="Iperius Remote">
                                            <span>{{ IDRemoteWithSpace(session.id_remote_host) }}</span>
                                        </div>
                                    </td>
                                    <td class="text-center">
                                        <label v-if="session.dt_start">
                                            {{ formatUtcDate(session.dt_start) }}
                                        </label>
                                        <span v-else>-</span>
                                    </td>
                                    <td class="text-center">
                                        <label v-if="session.dt_end">
                                            {{ formatUtcDate(session.dt_end) }}
                                        </label>
                                        <span v-else>-</span>
                                    </td>
                                    <td class="text-right text-bold pr-5">
                                        {{ formatHours(session.total_time) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <div class="my-4 text-right">
                    <button v-if="!isNewTicket" class="button alert" style="float:left" @click="deleteTicket">{{ $t("Delete") }}</button>
                    <router-link class="button" to="/tickets">{{ $t("Cancel") }}</router-link>
                    <button v-if="!isReadOnlyTicket" class="button secondary ml-2" :disabled="saving || !canSaveTicket()" @click="save">
                        {{ $t("Save") }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Navigation from '../utils/Navigation';
import SelectPCByGroup from '../utils/SelectPCByGroup.vue';
import moment from 'moment';
import { DefaultObjectBuilder } from '../../models/DefaulfObjectBuilder';
import {
    populateUsefulMaps,
    TICKET_PRIORITY,
    TICKET_PRIORITY_INFO,
    TICKET_STATUS,
    TICKET_STATUS_INFO
} from '../../utils/entityMethods/ticket';
import { getTeamMembers, getTeamsByLoggedUser, getTeamsWithGroup } from '../../utils/entityMethods/team';
import { getAllGroupsWithIdCustomer, getAllGroupsWithIdPC, getPCListByGroup } from '../../utils/entityMethods/group';
import { getPCListByIDCustomer } from '../../utils/entityMethods/computer';
import { insertSpacesEveryThreeDigits } from '../../utils/utilitiesmodule';

export default {
    name: "Ticket",
    components: { Navigation, SelectPCByGroup },
    props: {
        idTicket: {
            type: [String],
            default: null
        }
    },
    data() {
        return {
            TICKET_STATUS,
            TICKET_STATUS_INFO,
            TICKET_PRIORITY,
            TICKET_PRIORITY_INFO,
            priorityOptions: Object.values(TICKET_PRIORITY_INFO),
            selectableStatusOptions: [],

            loading: true,
            saving: false,
            ticket: null,
            customers: null,
            selectedCustomer: null,

            customerPCs: null,
            selectedPC: null,
            groupSelectorKey: 0,

            teams: [],
            selectedTeam: null,

            operators: [],
            selectedOperator: null,

            operatorPlaceholder: "",
            initialStatus: 0,
            initialIDOperator: null,
            isNewTicket: false,
            isUnassignedTicket: false,
        };
    },
    computed: {
        isReadOnlyTicket() {
            return !this.isNewTicket && this.ticket && [TICKET_STATUS.COMPLETED, TICKET_STATUS.CANCELLED].includes(Number(this.ticket.status));
        },
        userLoggedIsLead() {
            if (this.selectedTeam == null) {
                return false;
            }
            return this.selectedTeam.users.find(u => u.is_lead).id_user == this.$root.authObj.id_user;
        },
        // Etichetta testuale del PC risolto dal solo id_remote (usata quando non c'è un cliente
        // assegnato e quindi non possiamo popolare la select dei dispositivi del cliente).
        deviceLabel() {
            if (!this.ticket || !this.ticket.id_remote) return "";
            const pc = (this.$session.getPCList() || []).find(p => p.id_remote == this.ticket.id_remote);
            return pc ? `${pc.pc_name} (${pc.id_remote})` : this.ticket.id_remote;
        },
        customerLabel() {
            if (this.selectedCustomer) return this.selectedCustomer.company_name;
            const customer = this.ticket && this.$root.customerMap ? this.$root.customerMap[this.ticket.id_customer] : null;
            return customer ? customer.company_name : "-";
        },
        teamLabel() {
            if (this.selectedTeam) return this.selectedTeam.name;
            const team = this.ticket && this.$root.teamMap ? this.$root.teamMap[this.ticket.id_team] : null;
            return team ? team.name : "-";
        },
        operatorLabel() {
            if (this.selectedOperator) return this.userLabel(this.selectedOperator);
            const user = this.ticket && this.$root.userMap ? this.$root.userMap[this.ticket.id_user_assigned] : null;
            return user ? this.userLabel(user) : "-";
        },
        ticketSessions() {
            if (!this.ticket || !Array.isArray(this.ticket.sessions)) return [];
            return this.ticket.sessions;
        },
        ticketSessionsTotalTime() {
            return this.ticketSessions.reduce((sum, session) => sum + (Number(session.total_time) || 0), 0);
        },
        customerGroups() {
            this.groupSelectorKey;
            if (!this.ticket||!this.ticket.id_customer) return [];
            return (this.$session.getGroups()||[]).filter(group => group.id_customer==this.ticket.id_customer);
        },
        selectedPCId() {
            if (this.selectedPC) return this.selectedPC.id;
            if (!this.ticket||!this.ticket.id_remote) return null;
            const pc=this.$root.pcMap[this.ticket.id_remote];
            return pc? pc.id:null;
        }
    },
    async created() {
        this.ticket = await this.setTicket();
        this.initialStatus = this.ticket.status;
        this.initialIDOperator = this.ticket.id_operator;
        this.isNewTicket = !this.ticket.id;

        if (this.isNewTicket) {
            this.ticket.id_user_created = this.$root.authObj.id_user;
            this.customers = await this.$api.getCustomers();
            this.selectableStatusOptions = [TICKET_STATUS.PENDING, TICKET_STATUS.ASSIGNED];

            return;
        }

        this.selectableStatusOptions = [this.initialStatus].concat(
            this.isNewTicket
                ? [this.TICKET_STATUS.ASSIGNED]
                : TICKET_STATUS_INFO[this.initialStatus].nextStatusValues
        );

        // In modifica l'id_customer potrebbe non essere assegnato (ticket non ancora preso in carico):
        // in quel caso carico tutti i clienti per poterne scegliere uno, senza forzare una selezione.
        if (this.ticket.id_customer) {
            this.customers = [await this.$api.getCustomer(this.ticket.id_customer)];
            this.selectedCustomer = this.customers[0];
            if (this.isReadOnlyTicket) {
                this.setSelectedPCFromTicket();
            } else {
                this.onChangeCustomer();
            }
        } else {
            this.customers = await this.$api.getCustomers();
            this.selectedCustomer = null;
        }
    },
    mounted() {
        this.$root.$on("refreshCustomers", this.handleCustomerSaved);
    },
    beforeDestroy() {
        this.$root.$off("refreshCustomers", this.handleCustomerSaved);
    },
    methods: {
        getTeamsByLoggedUser,

        getTeamMembers,
        populateUsefulMaps,
        getPCListByGroup,
        getAllGroupsWithIdCustomer,
        getTeamsWithGroup,
        getPCListByIDCustomer,
        async setTicket() {
            if (!this.$root.checkAuth()) return;
            await this.populateUsefulMaps();

            let ticket = await this.resolveInitialTicket();
            if (!ticket) {
                this.loading = false;
                return;
            }
            return ticket;

        },
        async onChangeCustomer() {
            if (!this.selectedCustomer) return;
            const customerChanged=this.ticket.id_customer!=this.selectedCustomer.id;
            this.ticket.id_customer = this.selectedCustomer.id;

            if (customerChanged) {
                this.ticket.id_remote="";
                this.selectedPC=null;
            }
            this.setSelectedPCFromTicket();
            this.groupSelectorKey++;
            if (this.isUnassignedStatus(this.ticket.status)) {
                return;
            }
            this.teams.update(await this.$api.getTeamsWithIdCustomer(this.ticket.id_customer));

            if (this.ticket.id_team) {
                this.selectedTeam = this.teams.find(t => t.id == this.ticket.id_team);
                if (!this.selectedTeam) {
                    this.selectedTeam = this.teams[0];
                }
            } else {
                this.selectedTeam = this.teams[0];
            }
            this.onChangeTeam();
        },
        setSelectedPCFromTicket() {
            if (!this.ticket || !this.ticket.id_customer || !this.ticket.id_remote) {
                this.selectedPC=null;
                return;
            }
            this.customerPCs = this.getPCListByIDCustomer(this.ticket.id_customer) || [];
            this.selectedPC = this.customerPCs.find(pc => pc.id_remote == this.ticket.id_remote) || null;
        },
        onChangePC(pc) {
            this.selectedPC=pc||null;
            this.ticket.id_remote=pc&&pc.id_remote? pc.id_remote:"";
        },
        editSelectedCustomer() {
            if (!this.selectedCustomer) return;
            this.$root.$emit("OPENCUSTOMERDIALOG", this.selectedCustomer);
        },
        async handleCustomerSaved() {
            if (!this.ticket||!this.ticket.id_customer) return;
            const refreshedCustomer=await this.$api.getCustomer(this.ticket.id_customer);
            if (refreshedCustomer) {
                this.selectedCustomer=refreshedCustomer;
                const index=(this.customers||[]).findIndex(customer => customer.id==refreshedCustomer.id);
                if (index>=0) this.$set(this.customers, index, refreshedCustomer);
            }
            this.groupSelectorKey++;
            this.setSelectedPCFromTicket();
        },
        onChangeTeam() {
            this.ticket.id_team = this.selectedTeam.id;
            //Se l'utente loggato è un lead del team, ritorno tutti i membri
            //Se l'utente loggato è un semplice operatore, ritorno solo se stesso
            this.operators.update(this.getTeamMembers(this.selectedTeam, this.userLoggedIsLead));
            if (this.ticket.id_user_assigned) {
                this.selectedOperator = this.operators.find(m => m.id == this.ticket.id_user_assigned);
                if (!this.selectedOperator) {
                    this.selectedOperator = this.operators[0];
                }

            } else {
                this.selectedOperator = this.operators[0];

            }
            this.onChangeOperator();
        },
        onChangeOperator() {
            this.ticket.id_user_assigned = this.selectedOperator.id;
        },

        onChangeStatus() {
            var wasAssigned = !this.isUnassignedStatus(this.initialStatus)
            var isAssigned = !this.isUnassignedStatus(this.ticket.status)
            if (!isAssigned) {
                this.selectedTeam = null;
                this.operators.clear();
                this.selectedOperator = null;
                return;
            }

            if (!wasAssigned && isAssigned) {
                this.onChangeCustomer();
            }
        },


        async resolveInitialTicket() {
            /*const routedTicket = this.$route.params ? this.$route.params.ticket : null;
            if (routedTicket && (!this.idTicket || routedTicket.id == this.idTicket)) {
                return { ...routedTicket };
            }*/

            if (this.idTicket) {
                return await this.$api.getTicket(this.idTicket);
            }

            return DefaultObjectBuilder.getTicket();
        },
        isUnassignedStatus(status) {
            return [TICKET_STATUS.PENDING, TICKET_STATUS.RELEASED].includes(Number(status));
        },

        clearTicketAssignment() {
            this.ticket.id_team = null;
            this.ticket.id_user_assigned = null;
            this.operators.clear();
        },

        userLabel(user) {
            const fullName = [user.firstname, user.lastname].filter(Boolean).join(" ").trim();
            return fullName ? fullName + " (" + user.username + ")" : user.username;
        },
        sessionUserLabel(session) {
            if (!session) return "-";
            const name = [session.firstname, session.lastname].filter(Boolean).join(" ").trim();
            if (name) return name;

            const user = this.$root.userMap && session.id_user ? this.$root.userMap[session.id_user] : null;
            return user ? this.userLabel(user) : (session.id_user || "-");
        },
        IDRemoteWithSpace(value) {
            return value ? insertSpacesEveryThreeDigits(value) : "-";
        },
        formatUtcDate(d) {
            if (!d) return "-";
            return moment.utc(d).local().format("DD/MM/YYYY HH:mm");
        },
        formatHours(seconds) {
            if (seconds == null) return "-";
            const s = Math.floor(Number(seconds));
            if (s === 0) return "0s";
            const h = Math.floor(s / 3600);
            const m = Math.floor((s % 3600) / 60);
            const sec = s % 60;
            if (h > 0) return h + "h" + (m > 0 ? " " + m + "m" : "");
            if (m > 0) return m + "m" + (sec > 0 ? " " + sec + "s" : "");
            return sec + "s";
        },
        canSaveTicket() {
            if (this.isReadOnlyTicket) return false;
            if (!this.ticket.title || !this.ticket.description) return false;
            return true;
        },
        async save() {
            if (this.isReadOnlyTicket) return;
            if (this.saving || !this.canSaveTicket()) return;
            if (this.isUnassignedTicket) this.clearTicketAssignment();

            if (this.isUnassignedStatus(this.ticket.status)) {
                this.ticket.id_team = null;
                this.ticket.id_user_assigned = null;
            }
            
            const ticketToSave = { ...this.ticket };
            if (!ticketToSave.id_remote) delete ticketToSave.id_remote;
            this.saving = true;
            const saved = this.isNewTicket
                ? await this.$api.createTicket(ticketToSave)
                : await this.$api.editTicket(this.ticket.id, ticketToSave);
            this.saving = false;

            if (!saved) return;

            /*if (this.initialStatus != ticketToSave.status) {
                await this.$api.changeStatus(saved.id, ticketToSave.status);
            }

            if (this.initialIDOperator != ticketToSave.id_operator) {
                await this.$api.assignTo(saved.id, saved.id_team, saved.id_operator);
            }*/


            this.$router.push("/tickets");
        },
        async deleteTicket() {
            const dialogId = Math.floor(100000 + Math.random() * 900000).toString();
            const self = this;
            this.$root.$on(dialogId, async ok => {
                if (ok) {
                    const result = await self.$api.deleteTicket(self.ticket.id);
                    if (result) self.$router.push("/tickets");
                }
                self.$root.$off(dialogId);
            });
            this.$root.openYesNoDialog(
                dialogId,
                this.$t("Delete ticket"),
                this.$t("deleteConfirmation", { 0: this.ticket.title || ('#' + this.ticket.id) })
            );
        }
    }
};
</script>

<style scoped>
.ticket-scroll-page {
    height: 100vh;
    overflow-x: hidden;
    overflow-y: auto;
}

.ticket-page {
    max-width: 1100px;
    padding-bottom: 48px !important;
}

.ticket-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.ticket-section {
    background: #fff;
    border: 1px solid #d9d9d9;
}

.ticket-section-header {
    background: #f6f7f8;
    border-bottom: 1px solid #d9d9d9;
    padding: 12px 16px;
}

.ticket-section-body {
    padding: 16px;
}

.ticket-section-body .row:last-child {
    margin-bottom: 0 !important;
}

.ticket-actions {
    background: #fff;
    border: 1px solid #d9d9d9;
    padding: 16px;
}

textarea.input {
    min-height: 110px;
    resize: vertical;
}

.customer-select {
    flex: 1 1 auto;
    min-width: 0;
}

.customer-groups-warning {
    line-height: 1.4;
}

.ticket-sessions-table {
    margin-top: 0;
}

.ticket-sessions-table th,
.ticket-sessions-table td {
    vertical-align: middle;
}

.ticket-session-pc {
    min-width: 190px;
}

.ticket-session-device-line {
    align-items: center;
    display: flex;
    justify-content: flex-start;
    line-height: 1.35;
    min-height: 22px;
}

.ticket-session-remote-icon {
    height: 16px;
    width: 16px;
}

@media screen and (max-width: 768px) {
    .ticket-page {
        padding: 16px !important;
    }
}
</style>
